/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'apps/placeme/src/redux/hooks'
import { AddMapView, MapVisualizationModal } from '@dataplace.ai/ui-components/molecules'
import {
  LinkWithIconCheckOnMap,
  MapTile,
  Checkbox,
  NoPrint,
} from '@dataplace.ai/ui-components/atoms'
import { useTranslation } from 'react-i18next'
import { ResourceWithId } from '@dataplace.ai/ui-components/organisms/ResourcesSelector/@types/ResourceWithId'
import { IFeatureCollection } from '@dataplace.ai/ui-components/atoms/MapTile/components/MapOverlays/@types/IFeatureCollection'
import { Loader } from 'libs/shared/ui-components/src/atoms'
import { IGeojson } from '@dataplace.ai/ui-components/atoms/MapTile/components/MapOverlays/@types/IGeojson'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import { BasicLayers, FeatureCollectionLayers, GeojsonLayers, WMSLayers } from '@dataplace.ai/ui-components/atoms/MapTile/@types/LayersTypes'
import { TableFilter, usePersistingTableFilter } from '@dataplace.ai/ui-components/organisms/FilterableSortableTable/molecules'
import { useFeatureAccess } from '@dataplace.ai/functions/hooks/useFeatureAccess'
import { featureAccessIds } from 'apps/placeme/src/constants/constants'
import { poisTableData } from './data'
import { RootState } from '../../../../../../../redux/store'
import { fetchWorkspaceUsageValue, saveTileData } from '../../../../../slice/analysisSlice'
import { ComparedLocationHeader } from '../../../../atoms'
import { sortCategories, sortCategoriesNestedSelectList } from './functions'
import { ReportOutOfDatePoiSection } from '../../../../molecules'
import { Wrapper, NoItems, FilterWrapper, TableWrapper, MapWrapper, CheckboxWrapper, StyledTitleFormSectionSubTitle } from './PoisTile.styles'

import { PaginationTable } from './components/PaginationTable'
import { IPois, IPoisTileData, ISortableTableHeaderProps } from './@types/IPoisTileData'
import { headers } from './constants'

export const PoisTile: React.FC<{data: IPoisTileData, tileId: string}> = ({
  data, tileId,
}) => {
  // constants
  const { t } = useTranslation()
  const { isPrinting } = useCheckIfPrinting()
  const dispatch = useAppDispatch()

  const { value } = useSelector((state: RootState) => state.location)
  const {
    values, comparedLocation,
  } = useSelector((state: RootState) => state.analysis)

  const {
    nonGroupedLabels, groupedLabels,
  } = poisTableData

  const [grouped, setGrouped] = useState<boolean>(true)
  const [categories, setCategories] = useState<ResourceWithId[]>([])
  const [val, setValue] = useState<ResourceWithId[]>(categories)
  const [rowsToDisplay, setRowsToDisplay] = useState<{[key: string]: IPois[] }>({})
  const [comparedRowsToDisplay, setComparedRowsToDisplay] = useState<{[key: string]: IPois[] }>({})
  const labels = grouped ? groupedLabels : nonGroupedLabels
  const [isMapModalDisplayed, setIsMapModalDisplayed] = useState(false)
  const [isComparedMapModalDisplayed, setIsComparedMapModalDisplayed] = useState(false)
  const [isMapVisible, setIsMapVisible] = useState(false)
  const [isComparedMapVisible, setIsComparedMapVisible] = useState(false)
  const [filteredData, setFilteredData] = useState(data?.value?.pois ?? [])
  const [comparedFilteredData, comparedSetFilteredData] = useState(data?.value?.comparedLocation?.pois ?? [])

  const { hasFeatureAccess } = useFeatureAccess()
  const medicalInfoFeatureAccess = hasFeatureAccess(featureAccessIds.medicalInfo)

  // functions

  const catchmentId = values?.find(c => c.id === 'market')?.tiles?.find(t =>
    t.id === tileId)?.chosenRange?.catchmentId

  const handleMapOpen = (compared: boolean) => {
    if (compared) setIsComparedMapModalDisplayed(!isComparedMapModalDisplayed)
    else { setIsMapModalDisplayed(!isMapModalDisplayed) }
  }

  const handleAddMap = (compared: boolean) => {
    if (compared) setIsComparedMapVisible(true)
    else { setIsMapVisible(true) }
  }

  const getLayers = (compared: boolean) => {
    if ((!compared && data?.value?.pois?.length) || (compared && data?.value?.comparedLocation?.pois?.length)) {
      const items = compared ? comparedFilteredData : filteredData
      const features : IFeatureCollection['data']['features'] = []
      const tile = values?.find(c => c.id === 'market')?.tiles?.find(t =>
        t.id === tileId)
      const rangeCoords = compared
        ? tile?.comparedChosenRange?.geoJSON?.coordinates
        : tile?.chosenRange?.geoJSON?.coordinates
      const pois = items.filter(poi => val?.map(v => v?.id).includes(poi.category))
      pois?.forEach(poi => features.push({
        geometry: {
          coordinates: [poi.lng, poi.lat],
          type: 'Point',
        },
        properties: {
          title: `<div style="display: flex; flex-direction: column;"><span>${poi?.brand}</span><span>${poi?.address}</span>${poi?.salesArea ? `<span style="text-transform: lowercase;">${t('placeme.pois_tile.table_label_5')}</span>: ${poi.salesArea}</span>` : ''}</div>`,
          pinnedItem: {
            iconAnchor:[11, 11],
            class: 'poi-img',
            html: `<img alt='' src='${poi.logo || 'assets/icons/logoPlaceholder.svg'}' style="width:22px; height: 22px;"/>`,
          },
        },
        type: 'Feature',
      }))

      const layers: (BasicLayers | GeojsonLayers | WMSLayers | FeatureCollectionLayers)[] = [
        {
          id: 'pois-layer',
          layer: {
            data: {
              features,
              type: 'FeatureCollection',
            },
            options: {
              type: 'geojson',
              id: 'pois',
            },
          },
        },
      ]
      // range layer
      if (rangeCoords) {
        layers.push({
          id: (compared ? `${tile?.id}-compared` : tile?.id) || '',
          layer: {
            data: {
              coordinates: (rangeCoords) as IGeojson['data']['coordinates'],
              type: 'Polygon',
              properties: {},
            },
            options:{
              type: 'geojson',
              id: compared ? 'pois_range_compared' : 'pois_range',
              style: {
                color: '#0000a2',
                fillColor:'#0000a2',
                weight: 1.5,
                fillOpacity: 0.05,
              },
            },
          },
        })
      }

      return layers
    }
    return undefined
  }

  // ////////////////////////////////////////////////////////////////////////// Filter Settings
  const {
    tileSettings,
    popupRef,
    applyFiltersAndClose: _applyFiltersAndClose,
    deleteActiveFilter,
    tableFilterProps: {
      setTempFilter,
      filterData,
      tempFilter,
      ...tableFilterProps
    },
  } = usePersistingTableFilter({
    tileType: 'pois',
    tileId,
    headers: headers as ISortableTableHeaderProps[],
    setData: setFilteredData,
    data: data?.value.pois ?? [],
  })

  const applyFiltersAndClose = useCallback(() => {
    _applyFiltersAndClose({
      poisTableFilter: tempFilter,
    })
  }, [_applyFiltersAndClose, tempFilter])

  // ////////////////////////////////////////////////////////////////////////// Filter Settings END

  // ////////////////////////////////////////////////////////////////////////// Compared Filter Settings
  const {
    popupRef: comparedPopupRef,
    applyFiltersAndClose: _comparedApplyFiltersAndClose,
    deleteActiveFilter: comparedDeleteActiveFilter,
    tableFilterProps: {
      setTempFilter: comparedSetTempFilter,
      filterData: comparedFilterData,
      tempFilter: comparedTempFilter,
      ...comparedTableFilterProps
    },
  } = usePersistingTableFilter({
    tileType: 'pois',
    tileId,
    headers: headers as ISortableTableHeaderProps[],
    setData: comparedSetFilteredData,
    data: data?.value.comparedLocation?.pois ?? [],
  })

  const countPoisItems = (obj: Record<string, IPois[]>): number =>
    Object.values(obj).reduce((sum, arr) => sum + arr.length, 0)

  const filteredRows = useMemo(() => {
    const itemsSum = countPoisItems(rowsToDisplay) + countPoisItems(comparedRowsToDisplay)
    const hasActiveFilters = (tableFilterProps?.activeFilters ?? 0) > 0
    || (comparedTableFilterProps?.activeFilters ?? 0) > 0

    return hasActiveFilters ? itemsSum : 0
  }, [rowsToDisplay, comparedRowsToDisplay, tableFilterProps, comparedTableFilterProps])

  const comparedApplyFiltersAndClose = useCallback(() => {
    _comparedApplyFiltersAndClose({
      comparedPoisTableFilter: comparedTempFilter,
    })
  }, [_comparedApplyFiltersAndClose, comparedTempFilter])
  // ////////////////////////////////////////////////////////////////////////// Compared Filter Settings END

  // hooks

  useEffect(() => {
    const filterSettings = tileSettings.value?.poisTableFilter
    if (typeof filterSettings !== 'undefined') {
      setTempFilter(filterSettings)
      filterData()
    }
    const comparedFilterSettings = tileSettings.value?.comparedPoisTableFilter
    if (typeof comparedFilterSettings !== 'undefined') {
      comparedSetTempFilter(comparedFilterSettings)
      comparedFilterData()
    }
  }, [tileSettings.value, JSON.stringify(filteredData), JSON.stringify(comparedFilteredData)])

  useEffect(() => {
    if (!data?.mapLocation) {
      dispatch(fetchWorkspaceUsageValue())
    }
  }, [JSON.stringify(data)])

  useEffect(() => {
    if (data?.value?.pois?.length) {
      dispatch(saveTileData('market', tileId, {
        ...data,
        value: {
          ...data.value,
          filteredPoisLength: filteredRows,
        },
      }))
    }
  }, [filteredRows, data?.value?.pois?.length])

  useEffect(() => {
    if (!data?.value?.pois?.length || !filteredData) return

    // filtering results by selected categories
    const filteredValues = filteredData.filter(value => val
      .map(c => c.id).includes(value.category)).sort((a, b) => (a.distance - b.distance))
    // parsing an array to object by category
    const assignedValues = filteredValues.reduce((acc, cur) => ({
      ...acc,
      [cur.category]: acc?.[cur.category]?.length ? [...acc?.[cur.category], cur] : [cur],
    }), ({} as {[key: string]: IPois[] }))
    // grouping results by brand and counting
    const groupedValues = Object.entries(assignedValues).reduce((acc, cur) => {
      const grouped = cur[1].reduce((acc: IPois[], cur: IPois) => {
        const exist = acc.find(v => v.brand === cur.brand)
        const reducedValue = {
          ...cur,
          count: exist?.count ? exist.count + 1 : 1,
          distance: exist?.distance ? Math.min(exist.distance, cur.distance) : cur.distance,
        }
        let newArray = []
        if (exist) {
          acc.forEach(item => {
            if (item !== exist) newArray.push(item)
          })
          newArray.push(reducedValue)
        } else {
          newArray = [...acc, reducedValue]
        }

        return newArray.sort((a, b) => (a.distance - b.distance))
      }, [])

      return {
        ...acc,
        [cur[0]]: grouped,
      }
    }, {})
    // comparedLocation
    if (comparedFilteredData) {
      // filtering results by selected categories
      const filteredComparedValues = comparedFilteredData.filter(value => val
        .map(c => c.id).includes(value.category)).sort((a, b) => (a.distance - b.distance))
      // parsing an array to object by category
      const assignedComparedValues = filteredComparedValues.reduce((acc, cur) => ({
        ...acc,
        [cur.category]: acc?.[cur.category]?.length ? [...acc?.[cur.category], cur] : [cur],
      }), ({} as {[key: string]: IPois[] }))
      // grouping results by brand and counting
      const groupedValuesCompared = Object.entries(assignedComparedValues).reduce((acc, cur) => {
        const groupedCompared = cur[1].reduce((acc: IPois[], cur: IPois) => {
          const existCompared = acc.find(v => v.brand === cur.brand)
          const reducedValue = {
            ...cur,
            count: existCompared?.count ? existCompared.count + 1 : 1,
            distance: existCompared?.distance ? Math.min(existCompared.distance, cur.distance) : cur.distance,
          }
          let newArrayCompared = []
          if (existCompared) {
            acc.forEach(item => {
              if (item !== existCompared) newArrayCompared.push(item)
            })
            newArrayCompared.push(reducedValue)
          } else {
            newArrayCompared = [...acc, reducedValue]
          }

          return newArrayCompared.sort((a, b) => (a.distance - b.distance))
        }, [])

        return {
          ...acc,
          [cur[0]]: groupedCompared,
        }
      }, {})
      setComparedRowsToDisplay(grouped
        ? sortCategories(groupedValuesCompared)
        : sortCategories(filteredComparedValues.reduce((acc, cur) => ({
          ...acc,
          [cur.category]: acc?.[cur.category]?.length ? [...acc?.[cur.category], cur] : [cur],
        }), ({} as {[key: string]: IPois[] }))))
    }

    // setting the results to be displayed depending on whether they are to be grouped or not
    setRowsToDisplay(grouped
      ? sortCategories(groupedValues)
      : sortCategories(filteredValues.reduce((acc, cur) => ({
        ...acc,
        [cur.category]: acc?.[cur.category]?.length ? [...acc?.[cur.category], cur] : [cur],
      }), ({} as {[key: string]: IPois[] }))))
  }, [JSON.stringify(data), grouped, val, filteredData, comparedFilteredData])

  useEffect(() => {
    if (data?.value?.pois.length) {
      const categoriesObjects : { id: string, content: string}[] = []
      data?.value?.pois?.forEach(investment => {
        const categoryId = investment?.category
        if (!categoriesObjects.find(cat => cat?.id === categoryId)) {
          categoriesObjects.push({
            id: categoryId,
            content: t(`placeme.pois_tile.category.${categoryId.toLocaleLowerCase().split(/[_ -]+/)
              .join('-')}`),
          })
        }
      })
      // compared location
      if (comparedFilteredData) {
        comparedFilteredData.forEach(investment => {
          const categoryId = investment?.category
          if (!categoriesObjects.find(cat => cat?.id === categoryId)) {
            categoriesObjects.push({
              id: categoryId,
              content: t(`placeme.pois_tile.category.${categoryId.toLocaleLowerCase().split(/[_ -]+/)
                .join('-')}`),
            })
          }
        })
      }

      setCategories(sortCategoriesNestedSelectList(categoriesObjects))
      setValue(sortCategoriesNestedSelectList(categoriesObjects))
    }
  }, [filteredData, comparedFilteredData])

  useEffect(() => {
    if (!isPrinting) return
    handleAddMap(false)
    handleAddMap(true)
  }, [isPrinting])

  const mapLayers = useMemo(() => getLayers(false), [val, filteredData, JSON.stringify(data?.value?.pois)])
  const comparedMapLayers = useMemo(() => getLayers(true),
    [val, comparedFilteredData, JSON.stringify(data?.value?.comparedLocation?.pois)])

  return !data || data?.loading
    ? (<Loader />)
    : (data?.value?.pois?.length
      ? (
        <Wrapper>
          {/* report outdated poi modal section */}
          {catchmentId && <ReportOutOfDatePoiSection catchmentId={catchmentId} />}
          {data?.value?.comparedLocation && (
            <ComparedLocationHeader
              style={{
                marginTop: '1.5rem',
              }}
            >
              <h5>{t('placeme.municipality_population.compared_location.header_1')}</h5>
              {' '}
              <span>{value?.address}</span>
            </ComparedLocationHeader>
          )}
          <TableWrapper>
            <FilterWrapper>
              <TableFilter
                {...tableFilterProps}
                applyFiltersAndClose={applyFiltersAndClose}
                deleteActiveFilter={name => deleteActiveFilter('poisTableFilter', name)}
                headers={headers as ISortableTableHeaderProps[]}
                popupRef={popupRef}
              />
              <NoPrint>
                <CheckboxWrapper>
                  <Checkbox
                    checked={grouped}
                    onChange={(e) => setGrouped(e.target.checked)}
                  />
                  <span>{t('placeme.pois_tile.group')}</span>
                </CheckboxWrapper>
              </NoPrint>
            </FilterWrapper>
            {Object.entries(rowsToDisplay)?.map((entry) => (
              <PaginationTable
                key={entry[0]}
                category={entry[0]}
                grouped={grouped}
                labels={labels}
                medicalInfoFeatureAccess={medicalInfoFeatureAccess}
                poisData={entry[1]}
              />
            ))}
          </TableWrapper>
          {(!isMapVisible) && (
            <AddMapView
              buttonValue={0}
              description='placeme.add_traffic_visualisation_map.description'
              onChange={() => { handleAddMap(false) }}
              title='placeme.add_pois_map.title'
            />
          )}
          {isMapVisible
          && (
            <>
              <StyledTitleFormSectionSubTitle>
                <span className='avoid-page-break-after'>{t('placeme.pois_tile.points_on_map')}</span>
                <LinkWithIconCheckOnMap onClick={() => handleMapOpen(false)} />
              </StyledTitleFormSectionSubTitle>
              <MapWrapper $isPrinting={isPrinting}>
                <MapTile
                  dragging
                  height='100%'
                  layers={mapLayers}
                  location={value}
                  mapId='example-map-data-tile'
                  pinDisplayed
                  popupHeading={`${t('generic.chosen_location')}:`}
                  popupParagraph={value?.address}
                  showScaleControl
                  width='100%'
                  zoom={15}
                  zoomControl
                />
              </MapWrapper>
            </>
          )}
          {isMapModalDisplayed && (
            <MapVisualizationModal
              isDisplayed={isMapModalDisplayed}
              layers={mapLayers}
              location={value}
              mapId={`pois-map-${values?.find(c => c.id === 'market')?.tiles?.find(t => t.id === 'pois')?.chosenRange?.catchmentId}`}
              setIsDisplay={setIsMapModalDisplayed}
              zoom={15}
            />
          )}

          {data?.value?.comparedLocation
          && (
            <div>
              <ComparedLocationHeader
                second
                style={{
                  marginTop: '1.5rem',
                }}
              >
                <h5>{t('placeme.municipality_population.compared_location.header_2')}</h5>
                {' '}
                <span>{comparedLocation?.location?.address}</span>
              </ComparedLocationHeader>
              <TableWrapper>
                <FilterWrapper>
                  <TableFilter
                    {...comparedTableFilterProps}
                    applyFiltersAndClose={comparedApplyFiltersAndClose}
                    deleteActiveFilter={name => comparedDeleteActiveFilter('comparedPoisTableFilter', name)}
                    headers={headers as ISortableTableHeaderProps[]}
                    popupRef={comparedPopupRef}
                  />
                  <NoPrint>
                    <CheckboxWrapper>
                      <Checkbox
                        checked={grouped}
                        onClick={() => setGrouped(state => !state)}
                      />
                      <span>{t('placeme.pois_tile.group')}</span>
                    </CheckboxWrapper>
                  </NoPrint>
                </FilterWrapper>
                {Object.entries(comparedRowsToDisplay)?.map((entry) => (
                  <PaginationTable
                    key={entry[0]}
                    category={entry[0]}
                    grouped={grouped}
                    labels={labels}
                    medicalInfoFeatureAccess={medicalInfoFeatureAccess}
                    poisData={entry[1]}
                  />
                ))}
              </TableWrapper>
              {(!isComparedMapVisible) && (
                <AddMapView
                  buttonValue={0}
                  description='placeme.add_traffic_visualisation_map.description'
                  onChange={() => handleAddMap(true)}
                  title='placeme.add_pois_map.title'
                />
              )}
              {isComparedMapVisible
            && (
              <>
                <StyledTitleFormSectionSubTitle>
                  <span className='avoid-page-break-after'>{t('placeme.pois_tile.points_on_map')}</span>
                  <LinkWithIconCheckOnMap onClick={() => handleMapOpen(true)} />
                </StyledTitleFormSectionSubTitle>
                <MapWrapper $isPrinting={isPrinting}>
                  <MapTile
                    dragging
                    height='100%'
                    layers={comparedMapLayers}
                    location={comparedLocation?.location}
                    mapId='example-map-data-tile'
                    pinDisplayed
                    popupHeading={`${t('generic.chosen_location')}:`}
                    popupParagraph={comparedLocation?.location?.address}
                    showScaleControl
                    width='100%'
                    zoom={15}
                    zoomControl
                  />
                </MapWrapper>
              </>
            )}
              {isComparedMapModalDisplayed && (
                <MapVisualizationModal
                  isDisplayed={isComparedMapModalDisplayed}
                  layers={comparedMapLayers}
                  location={comparedLocation?.location}
                  mapId={`pois-map-${values?.find(c => c.id === 'market')?.tiles?.find(t => t.id === 'pois')?.chosenRange?.catchmentId}_compared`}
                  setIsDisplay={setIsComparedMapModalDisplayed}
                  zoom={15}
                />
              )}
            </div>
          )}

        </Wrapper>
      )
      : (
        <Wrapper>
          <NoItems>{t('placeme.pois.no_items')}</NoItems>
        </Wrapper>
      )
    )
}
